import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useDisclosure } from '@nextui-org/react';
import { useQuery, useMutation } from '@apollo/client';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useMediaQuery } from 'react-responsive';
import { toast } from 'react-toastify';
import { Beef, Check, ClipboardList, Droplet, Lock, Star, Utensils, Wheat } from 'lucide-react';

import { GET_DIET_PLAN, GET_CALORIE_LOGS, GET_ME } from '../graphql/queries';
import { CREATE_CALORIE_LOG, UPDATE_CALORIE_LOG, DELETE_CALORIE_LOG } from '../graphql/mutations';
import { trackPageView, trackEvent } from '../utils/gt';
import {
  getCurrentWeekRange,
  getDateForDayIndex,
  getDayDate,
  getTodayIndex,
  isGoalHit,
  numberFormatter,
  planFoodToEntry,
  removeMealEntry,
  restoreEntry,
  sumEntries,
} from '../utils/calorieLog';
import { useCountUp } from '../utils/motion';
import { isProAccount } from '../utils/workoutLock';
import { trialContext } from '../utils/subscription';
import { showUndoToast } from '../utils/undoToast';

import { AddFoodSheet } from '../components/calories/AddFoodSheet';
import { CalorieRing } from '../components/calories/CalorieRing';
import { MealTimeline } from '../components/calories/MealTimeline';
import { NextMealCard } from '../components/calories/NextMealCard';
import { Burst } from '../components/app/Burst';
import { ConfirmDialog } from '../components/app/ConfirmDialog';
import '../styles/pages/calorias.css';

const DAY_KEYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
const DAY_SHORT_KEYS = ['monShort', 'tueShort', 'wedShort', 'thuShort', 'friShort', 'satShort', 'sunShort'];

const MACROS = [
  { key: 'protein', Icon: Beef },
  { key: 'carbs', Icon: Wheat },
  { key: 'fat', Icon: Droplet },
];

const NO_PLAN_POINTS = ['noPlanPoint1', 'noPlanPoint2', 'noPlanPoint3'];

export const CalorieTracker = () => {
  const { t, i18n: { language } } = useTranslation();
  const navigate = useNavigate();
  const isDesktop = useMediaQuery({ query: '(min-width: 960px)' });
  const ui = (key, options) => t(`pages.CalorieTracker.ui.${key}`, options);
  const locale = language === 'pt' ? 'pt-BR' : 'en-US';
  const formatNumber = numberFormatter(language);
  const todayIndex = getTodayIndex();

  // State
  // A link can open a day (?day=), a meal (?meal=) and its add sheet (?add=1): the diet plan's links do
  const [searchParams, setSearchParams] = useSearchParams();
  const linkedDay = Number(searchParams.get('day'));
  const linkedMeal = Number(searchParams.get('meal'));
  const hasLinkedDay = searchParams.has('day') && Number.isInteger(linkedDay) && linkedDay >= 0 && linkedDay <= todayIndex;
  const hasLinkedMeal = searchParams.has('meal') && Number.isInteger(linkedMeal) && linkedMeal >= 0;
  const [selectedDay, setSelectedDay] = useState(hasLinkedDay ? linkedDay : todayIndex);
  // Meals opened by a tap, and (desktop, where eaten meals start open) eaten meals closed by one
  const [openMeals, setOpenMeals] = useState(() => new Set(hasLinkedMeal ? [linkedMeal] : []));
  const [closedMeals, setClosedMeals] = useState(() => new Set());
  const [sheet, setSheet] = useState({ mealIndex: null, tab: 'plan' });
  // The meal whose "Comi o planejado" is saving, for its spinner
  const [loggingPlanned, setLoggingPlanned] = useState(null);
  // Set when an added food lands the day inside its target: a one-off burst
  const [celebratedAt, setCelebratedAt] = useState(null);
  const { isOpen: isSheetOpen, onOpen: onSheetOpen, onOpenChange: onSheetOpenChange, onClose: onSheetClose } = useDisclosure();

  // Data fetching
  const { data: dataDietPlan, loading: isLoadingDietPlan } = useQuery(GET_DIET_PLAN);
  const { data: dataMe } = useQuery(GET_ME, { fetchPolicy: 'cache-first' });

  // selectedDay dep forces recomputation on tab change to handle midnight/week boundary crossover
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const weekRange = useMemo(() => getCurrentWeekRange(), [selectedDay]);
  const {
    data: dataCalorieLogs,
    loading: isLoadingCalorieLogs,
    refetch: refetchCalorieLogs,
  } = useQuery(GET_CALORIE_LOGS, {
    variables: { dateGte: weekRange.dateGte, dateLte: weekRange.dateLte },
  });

  // Mutations
  const [createCalorieLogMutation, { loading: isLoadingCreateLog }] = useMutation(CREATE_CALORIE_LOG);
  const [updateCalorieLogMutation, { loading: isLoadingUpdateLog }] = useMutation(UPDATE_CALORIE_LOG);
  const [deleteCalorieLogMutation, { loading: isLoadingDeleteLog }] = useMutation(DELETE_CALORIE_LOG);

  const isMutating = isLoadingCreateLog || isLoadingUpdateLog || isLoadingDeleteLog;

  // Analytics
  useEffect(() => {
    trackPageView('calorie_tracker', `${t('appUrl')}/calorie-tracker`, '/calorie-tracker');
  }, [t]);

  // Derived data
  const dietPlan = dataDietPlan?.dietPlan?.data;
  const weekPlan = dietPlan?.attributes?.weekPlan;
  // A plan kept from a Pro that ended comes without its week (the API keeps the meals until the account is Pro again)
  const isKept = !!dietPlan && !!dataMe?.me && !isProAccount(dataMe.me);
  const targets = dietPlan?.attributes?.nutritionData;
  const targetKcal = targets?.targetCalories;
  const calorieLogs = dataCalorieLogs?.calorieLogs?.data;

  // The latest logs, for an undo that runs after the week has been fetched again
  const logsRef = useRef(calorieLogs);
  logsRef.current = calorieLogs;

  /**
   * Get the calorie log for a specific date, if it exists.
   */
  const getLogForDate = useCallback(
    (dateStr) => {
      if (!calorieLogs) return null;
      return calorieLogs.find((log) => log.attributes.date === dateStr) || null;
    },
    [calorieLogs]
  );

  /**
   * Compute the selected day's data: diet plan meals + user-logged entries.
   */
  const selectedDayData = useMemo(() => {
    if (!weekPlan?.[selectedDay]) return null;
    const day = weekPlan[selectedDay];
    const dateStr = getDateForDayIndex(selectedDay);
    const log = getLogForDate(dateStr);
    const userEntries = log?.attributes?.entries || [];

    // Group user entries by mealIndex so each meal shows its own logged foods
    const mealsWithTotals = day.meals.map((meal, mealIdx) => {
      const mealEntries = userEntries.filter((entry) => (entry.mealIndex ?? 0) === mealIdx);
      return { ...meal, mealEntries, totals: sumEntries(mealEntries) };
    });

    // Day totals only reflect user-logged entries
    return { ...day, mealsWithTotals, dayTotals: sumEntries(userEntries), userEntries, log, dateStr };
  }, [weekPlan, selectedDay, getLogForDate]);

  const eatenCalories = selectedDayData?.dayTotals.calories ?? 0;
  const shownEaten = useCountUp(Math.round(eatenCalories));

  const openSheet = (mealIndex, tab = 'plan') => {
    setSheet({ mealIndex, tab });
    onSheetOpen();
  };

  useEffect(() => {
    if (!weekPlan || !searchParams.toString()) return;
    if (searchParams.get('add') === '1' && hasLinkedMeal && weekPlan[selectedDay]?.meals?.[linkedMeal]) {
      openSheet(linkedMeal, weekPlan[selectedDay].meals[linkedMeal].isFreeMeal ? 'search' : 'plan');
    }
    setSearchParams({}, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [weekPlan]);

  useEffect(() => {
    if (!celebratedAt) return undefined;
    const timer = setTimeout(() => setCelebratedAt(null), 1800);
    return () => clearTimeout(timer);
  }, [celebratedAt]);

  // Phones open a meal on tap; desktop starts with the eaten meals open
  const isMealOpen = (mealIndex, isDone) => openMeals.has(mealIndex) || (isDesktop && isDone && !closedMeals.has(mealIndex));

  // Toggle meal expanded/collapsed
  const toggleMeal = (mealIndex, isDone) => {
    trackEvent('calorie_meal_expand', `Calorie Tracker - Expand Meal ${mealIndex + 1}`, 'calorie_tracker', true);
    const without = (set) => {
      const next = new Set(set);
      next.delete(mealIndex);
      return next;
    };
    const withIt = (set) => new Set(set).add(mealIndex);
    if (isMealOpen(mealIndex, isDone)) {
      setOpenMeals(without);
      setClosedMeals(withIt);
    } else {
      setClosedMeals(without);
      setOpenMeals(withIt);
    }
  };

  // Handle day change
  const handleDayChange = (idx) => {
    trackEvent('calorie_day_change', `Calorie Tracker - Day ${DAY_KEYS[idx]}`, 'calorie_tracker', true);
    setSelectedDay(idx);
    setOpenMeals(new Set());
    setClosedMeals(new Set());
  };

  /**
   * Save a day's whole list of entries: creates its log on the first food and deletes it when the last one goes.
   */
  const saveDayEntries = async (dateStr, entries) => {
    const existingLog = (logsRef.current || []).find((log) => log.attributes.date === dateStr);
    if (existingLog && entries.length === 0) {
      await deleteCalorieLogMutation({ variables: { id: existingLog.id } });
    } else if (existingLog) {
      await updateCalorieLogMutation({ variables: { id: existingLog.id, data: { entries, totalCalories: sumEntries(entries).calories } } });
    } else {
      await createCalorieLogMutation({ variables: { data: { date: dateStr, entries, totalCalories: sumEntries(entries).calories } } });
    }
    await refetchCalorieLogs();
  };

  // A burst (and its event) when the foods just added land the day inside its target
  const celebrateIfGoalHit = (addedCalories) => {
    if (!isGoalHit(eatenCalories, targetKcal) && isGoalHit(eatenCalories + addedCalories, targetKcal)) {
      trackEvent('calorie_goal_hit', 'Calorie Tracker - Daily Goal Hit', 'calorie_tracker', true);
      setCelebratedAt(Date.now());
    }
  };

  // The sheet's selection, planned and searched foods together, into its meal
  const handleAddEntries = async (entries) => {
    trackEvent('calorie_entry_add', 'Calorie Tracker - Add Food Entry', 'calorie_tracker', true);
    const mealIndex = sheet.mealIndex;
    const dateStr = getDateForDayIndex(selectedDay);
    const newEntries = entries.map((entry) => ({ ...entry, mealIndex }));
    const existingEntries = getLogForDate(dateStr)?.attributes?.entries || [];

    try {
      await saveDayEntries(dateStr, [...existingEntries, ...newEntries]);
      toast.success(ui('toastAdded', { count: newEntries.length }));
      celebrateIfGoalHit(sumEntries(newEntries).calories);
      setOpenMeals((prev) => new Set(prev).add(mealIndex));
      setClosedMeals((prev) => {
        const next = new Set(prev);
        next.delete(mealIndex);
        return next;
      });
      onSheetClose();
    } catch {
      toast.error(ui('saveError'));
    }
  };

  // The planned meal, all its foods at once
  const handleLogPlannedMeal = async (meal, mealIndex) => {
    trackEvent('calorie_log_planned_meal', 'Calorie Tracker - Log Planned Meal', 'calorie_tracker', true);
    const dateStr = getDateForDayIndex(selectedDay);
    const newEntries = meal.foods.filter((food) => food.name).map((food) => planFoodToEntry(food, mealIndex));
    const existingEntries = getLogForDate(dateStr)?.attributes?.entries || [];
    setLoggingPlanned(mealIndex);
    try {
      await saveDayEntries(dateStr, [...existingEntries, ...newEntries]);
      toast.success(ui('toastPlanned', { meal: meal.name }));
      celebrateIfGoalHit(sumEntries(newEntries).calories);
    } catch {
      toast.error(ui('saveError'));
    }
    setLoggingPlanned(null);
  };

  // Put a removed food back where it was, on the log as it is now
  const handleUndoDelete = async (dateStr, entry, at) => {
    trackEvent('calorie_entry_undo', 'Calorie Tracker - Undo Delete Food Entry', 'calorie_tracker', true);
    const current = (logsRef.current || []).find((log) => log.attributes.date === dateStr)?.attributes?.entries || [];
    try {
      await saveDayEntries(dateStr, restoreEntry(current, entry, at));
    } catch {
      toast.error(ui('saveError'));
    }
  };

  // Remove one food from a meal, with a few seconds to undo it
  // A food waiting for "Remover" in the confirm dialog: { mealIndex, entryIndex, name }
  const [pendingRemoval, setPendingRemoval] = useState(null);
  const askDeleteEntry = (mealIndex, entryIndex) => {
    const entries = getLogForDate(getDateForDayIndex(selectedDay))?.attributes?.entries || [];
    setPendingRemoval({ mealIndex, entryIndex, name: removeMealEntry(entries, mealIndex, entryIndex)?.removed?.name || '' });
  };

  const handleDeleteEntry = async (mealIndex, mealEntryIndex) => {
    trackEvent('calorie_entry_delete', 'Calorie Tracker - Delete Food Entry', 'calorie_tracker', true);
    const dateStr = getDateForDayIndex(selectedDay);
    const existingLog = getLogForDate(dateStr);
    if (!existingLog) return;
    const removal = removeMealEntry(existingLog.attributes.entries || [], mealIndex, mealEntryIndex);
    if (!removal) return;

    try {
      await saveDayEntries(dateStr, removal.entries);
      showUndoToast(ui('toastRemoved', { name: removal.removed.name }), {
        undoLabel: ui('undo'),
        onUndo: () => handleUndoDelete(dateStr, removal.removed, removal.at),
      });
    } catch {
      toast.error(ui('saveError'));
    }
  };

  const changePlan = () => {
    trackEvent('calorie_change_plan', 'Calorie Tracker - Change Plan', 'calorie_tracker', true);
    navigate('/diet');
  };

  const dayName = (idx) => t(`pages.DietPlan.${DAY_KEYS[idx]}`);
  const longDate = (idx) => getDayDate(idx).toLocaleDateString(locale, { day: 'numeric', month: 'long' });
  const dateLine = selectedDay === todayIndex ? ui('todayDate', { date: longDate(selectedDay) }) : ui('dayDate', { day: dayName(selectedDay), date: longDate(selectedDay) });

  // A tab of its own (the phone bar, the desktop rail): no way back, just the way to the plan beside the title
  const pageHead = (withPlanButton) => (
    <div className="mt-cal-top">
      <div className="mt-cal-head">
        <h1 className="mt-cal-head__title">{ui('title')}</h1>
        <p className="mt-cal-head__date">{dateLine}</p>
      </div>
      {withPlanButton &&
        (isDesktop ? (
          <button type="button" className="mt-btn mt-btn--quiet mt-cal-btn" onClick={changePlan}>
            <ClipboardList aria-hidden="true" />
            {ui('changePlan')}
          </button>
        ) : (
          <button type="button" className="mt-cal-link mt-cal-top__plan" onClick={changePlan}>
            <ClipboardList aria-hidden="true" />
            {ui('changePlanShort')}
          </button>
        ))}
    </div>
  );

  // Loading state
  if (isLoadingDietPlan || isLoadingCalorieLogs) {
    return (
      <div className="mt-root mt-root--app mt-cal-page">
        {pageHead(false)}
        <div className="mt-builder__status" aria-busy="true">
          <span className="mt-builder__spinner" aria-hidden="true" />
        </div>
      </div>
    );
  }

  // A kept plan: back as it was with Pro, nothing to log against until then
  if (isKept) {
    return (
      <div className="mt-root mt-root--app mt-cal-page">
        {pageHead(false)}
        <section className="mt-cal-none" aria-labelledby="mt-cal-none-title">
          <CalorieRing share={0} size={180} stroke={16} className="mt-cal-none__ring">
            <Lock className="mt-cal-none__icon" aria-hidden="true" />
          </CalorieRing>
          <h2 id="mt-cal-none-title" className="mt-cal-none__title">
            {t('pages.DietPlan.ui.keptTitle')}
          </h2>
          <p className="mt-cal-none__text">{t('pages.DietPlan.ui.keptBody')}</p>
          <button
            type="button"
            className="mt-btn mt-btn--primary mt-cal-btn mt-cal-btn--go"
            onClick={() => {
              trackEvent('calorie_kept_plan_cta', 'Calorie Tracker - Kept Plan CTA', 'calorie_tracker', true);
              navigate('/plans');
            }}
          >
            {t('components.ModalUpgradePro.cta', { context: trialContext(dataMe.me) })}
          </button>
        </section>
      </div>
    );
  }

  // No diet plan state (or one whose week hasn't been written yet)
  if (!dietPlan || !weekPlan?.length) {
    return (
      <div className="mt-root mt-root--app mt-cal-page">
        {pageHead(false)}
        <section className="mt-cal-none" aria-labelledby="mt-cal-none-title">
          <CalorieRing share={0} size={180} stroke={16} className="mt-cal-none__ring">
            <Utensils className="mt-cal-none__icon" aria-hidden="true" />
          </CalorieRing>
          <h2 id="mt-cal-none-title" className="mt-cal-none__title">
            {ui('noPlanTitle')}
          </h2>
          <p className="mt-cal-none__text">{ui('noPlanText')}</p>
          <ul className="mt-cal-none__points">
            {NO_PLAN_POINTS.map((key) => (
              <li key={key}>
                <Check aria-hidden="true" />
                {ui(key)}
              </li>
            ))}
          </ul>
          <button type="button" className="mt-btn mt-btn--primary mt-cal-btn mt-cal-btn--go" onClick={() => navigate('/diet')}>
            {ui('createPlan')}
          </button>
        </section>
      </div>
    );
  }

  if (!selectedDayData) return null;

  // Today and earlier days of this week can be logged; the rest of the week hasn't happened yet
  const canLog = selectedDay <= todayIndex;
  const isPast = selectedDay < todayIndex;
  const eaten = selectedDayData.dayTotals;
  const progress = targetKcal ? eaten.calories / targetKcal : 0;
  const isHit = isGoalHit(eaten.calories, targetKcal);
  const meals = selectedDayData.mealsWithTotals;
  // The meal to eat next: today's first meal with nothing logged
  const nextIndex = selectedDay === todayIndex ? meals.findIndex((meal) => meal.mealEntries.length === 0) : -1;

  let leftLine = null;
  if (targetKcal) {
    const diff = targetKcal - eaten.calories;
    if (diff < 0) leftLine = ui('over', { kcal: formatNumber(-diff) });
    else if (isPast) leftLine = ui('under', { kcal: formatNumber(diff) });
    else leftLine = ui('left', { kcal: formatNumber(diff) });
  }

  const weekRow = (
    <div className="mt-cal-week" role="group" aria-label={ui('dayPills')}>
      {DAY_KEYS.map((key, idx) => {
        const dateStr = getDateForDayIndex(idx);
        const dayNumber = getDayDate(idx).getDate();
        const dayKcal = sumEntries(getLogForDate(dateStr)?.attributes?.entries).calories;
        const isLoggable = idx <= todayIndex;
        const hasFree = weekPlan[idx]?.meals?.some((meal) => meal.isFreeMeal);
        return (
          <button
            key={key}
            type="button"
            className="mt-cal-wd"
            aria-pressed={selectedDay === idx}
            aria-label={
              isLoggable
                ? ui('dayButton', { day: dayName(idx), date: dayNumber, kcal: formatNumber(dayKcal) })
                : ui('dayButtonLater', { day: dayName(idx), date: dayNumber })
            }
            onClick={() => handleDayChange(idx)}
            disabled={!isLoggable}
          >
            <CalorieRing share={targetKcal ? dayKcal / targetKcal : 0} size={38} stroke={4.5} className={isGoalHit(dayKcal, targetKcal) ? 'is-hit' : ''}>
              <b className="mt-cal-wd__num">{dayNumber}</b>
            </CalorieRing>
            <span className="mt-cal-wd__label">
              {t(`pages.DietPlan.${DAY_SHORT_KEYS[idx]}`)}
              {hasFree && <Star className="mt-cal-wd__star" aria-hidden="true" />}
            </span>
          </button>
        );
      })}
    </div>
  );

  const summary = (
    <>
      <section className="mt-cal-hero" aria-label={ui('summary')}>
        <CalorieRing share={progress} size={isDesktop ? 248 : 216} stroke={isDesktop ? 20 : 18} className="mt-cal-hero__ring">
          <span className="mt-cal-hero__inner">
            {isHit && <Check className="mt-cal-hero__check" aria-hidden="true" />}
            <span className="mt-cal-hero__kcal">{formatNumber(shownEaten)}</span>
            {targetKcal ? <span className="mt-cal-hero__target">{ui('ofTarget', { kcal: formatNumber(targetKcal) })}</span> : null}
          </span>
          {celebratedAt && <Burst key={celebratedAt} />}
        </CalorieRing>
        {leftLine && (
          <p className="mt-cal-hero__left">
            {leftLine} <span>· {Math.round(progress * 100)}%</span>
          </p>
        )}
        {isHit && (
          <p className="mt-cal-pill mt-cal-hero__goal">
            <Check aria-hidden="true" />
            {ui('goalHit')}
          </p>
        )}
      </section>
      <ul className="mt-cal-macros">
        {MACROS.map(({ key, Icon }) => {
          const target = targets?.macros?.[key];
          return (
            <li key={key}>
              <CalorieRing share={target ? eaten[key] / target : 0} size={isDesktop ? 66 : 62} stroke={6.5}>
                <Icon className="mt-cal-macros__icon" aria-hidden="true" />
              </CalorieRing>
              <span className="mt-cal-macros__name">{t(`pages.DietPlan.${key}`)}</span>
              <span className="mt-cal-macros__grams">
                {formatNumber(eaten[key])}
                <small>{target ? ` / ${formatNumber(target)} g` : ' g'}</small>
              </span>
            </li>
          );
        })}
      </ul>
    </>
  );

  // A day that's over gets one line on how it closed
  let dayNote = null;
  if (isPast && targetKcal) {
    const diff = eaten.calories - targetKcal;
    dayNote = (
      <p className="mt-cal-closed">
        {isHit ? (
          <>
            <b>{ui('closedHit')}</b> {ui('closedAt', { day: dayName(selectedDay) })} {ui('kcal', { kcal: formatNumber(eaten.calories) })}.
          </>
        ) : (
          <>
            {ui('closedAt', { day: dayName(selectedDay) })} <b>{ui('kcal', { kcal: formatNumber(eaten.calories) })}</b>,{' '}
            {diff < 0 ? ui('closedUnder', { kcal: formatNumber(-diff) }) : ui('closedOver', { kcal: formatNumber(diff) })}
          </>
        )}
      </p>
    );
  }

  const nextCard =
    nextIndex >= 0 ? (
      <NextMealCard
        meal={meals[nextIndex]}
        mealIndex={nextIndex}
        onLogPlanned={handleLogPlannedMeal}
        onAdd={openSheet}
        isSaving={isMutating}
        isLoggingPlanned={loggingPlanned === nextIndex}
      />
    ) : null;

  const dayTitle = (
    <div className="mt-cal-sec">
      <h2>{selectedDay === todayIndex ? ui('yourDay') : dayName(selectedDay)}</h2>
      {targetKcal ? <span>{ui('dayOf', { kcal: formatNumber(eaten.calories), target: formatNumber(targetKcal) })}</span> : null}
    </div>
  );

  const timeline = (
    <MealTimeline
      meals={meals}
      nextIndex={nextIndex}
      isPast={isPast}
      canLog={canLog}
      isOpen={isMealOpen}
      onToggle={toggleMeal}
      onLogPlanned={handleLogPlannedMeal}
      onAdd={openSheet}
      onDelete={askDeleteEntry}
      isSaving={isMutating}
      loggingPlanned={loggingPlanned}
    />
  );

  return (
    <div className="mt-root mt-root--app mt-cal-page">
      {pageHead(true)}

      {isDesktop ? (
        <div className="mt-cal-layout">
          <div className="mt-cal-side">
            {summary}
            {weekRow}
          </div>
          <div className="mt-cal-main">
            {nextCard || dayNote}
            {dayTitle}
            {timeline}
          </div>
        </div>
      ) : (
        <>
          {weekRow}
          {summary}
          {nextCard}
          {dayNote}
          {dayTitle}
          {timeline}
        </>
      )}

      <ConfirmDialog
        isOpen={!!pendingRemoval}
        title={ui('removeConfirmTitle', { name: pendingRemoval?.name })}
        text={ui('removeConfirmText')}
        confirmLabel={ui('removeConfirm')}
        cancelLabel={ui('removeCancel')}
        onCancel={() => setPendingRemoval(null)}
        onConfirm={() => {
          const { mealIndex, entryIndex } = pendingRemoval;
          setPendingRemoval(null);
          handleDeleteEntry(mealIndex, entryIndex);
        }}
      />
      <AddFoodSheet
        isOpen={isSheetOpen}
        onOpenChange={onSheetOpenChange}
        meal={sheet.mealIndex != null ? meals[sheet.mealIndex] : null}
        dayName={selectedDay === todayIndex ? ui('todayLower') : language === 'pt' ? dayName(selectedDay).toLowerCase() : dayName(selectedDay)}
        initialTab={sheet.tab}
        onAdd={handleAddEntries}
        isSaving={isMutating}
      />
    </div>
  );
};
